import React, { useState } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { JOBTREAD_IMPORT_TYPES } from '../../../services/technologyCommunityService';
import { getFileKindForTab } from '../../../services/templatesImportsService';

const AUTOMATION_DEPARTMENTS = [
  { value: 'lead', label: 'Lead' },
  { value: 'sales', label: 'Sales' },
  { value: 'operations', label: 'Operations' },
  { value: 'finance', label: 'Finance' },
  { value: 'hr', label: 'HR' },
  { value: 'marketing', label: 'Marketing' },
];

const TechnologyUploadModal = ({ isOpen, onClose, type, onSubmit, submitting = false }) => {
  const { darkMode } = useTheme();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [importType, setImportType] = useState('cost-groups');
  const [category, setCategory] = useState('lead');
  const [appsUsed, setAppsUsed] = useState('');
  const [workflowSteps, setWorkflowSteps] = useState([{ title: '', description: '' }]);
  const [file, setFile] = useState(null);
  const [folderZip, setFolderZip] = useState(null);
  const [instructionsFile, setInstructionsFile] = useState(null);
  const [demoVideo, setDemoVideo] = useState(null);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const reset = () => {
    setTitle('');
    setDescription('');
    setImportType('cost-groups');
    setCategory('lead');
    setAppsUsed('');
    setWorkflowSteps([{ title: '', description: '' }]);
    setFile(null);
    setFolderZip(null);
    setInstructionsFile(null);
    setDemoVideo(null);
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await onSubmit({
        title,
        description,
        importType,
        category,
        appsUsed,
        workflowSteps: workflowSteps.filter((s) => s.title.trim()),
        file,
        folderZip,
        instructionsFile,
        demoVideo,
      });
      reset();
      onClose();
    } catch (err) {
      setError(err.message || 'Upload failed.');
    }
  };

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const titles = {
    'jobtread-import': 'Share JobTread Import',
    automation: 'Share Automation Template',
    app: 'Share Tech App',
    'claude-skill': 'Share Skill',
  };

  const fileKind = type === 'jobtread-import' ? getFileKindForTab(importType) : null;
  const acceptFile =
    type === 'jobtread-import'
      ? fileKind === 'json'
        ? '.json'
        : '.csv'
      : type === 'claude-skill'
        ? '.skill'
        : undefined;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl`}>
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {titles[type] || 'Share Resource'}
          </h2>
          <button type="button" onClick={handleClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-200">
              {error}
            </div>
          )}

          <div>
            <label className={labelClass}>Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} required />
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputClass}
              rows={3}
              placeholder="What does this resource do?"
            />
          </div>

          {type === 'jobtread-import' && (
            <div>
              <label className={labelClass}>Import Type</label>
              <select value={importType} onChange={(e) => setImportType(e.target.value)} className={inputClass}>
                {JOBTREAD_IMPORT_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.emoji ? `${t.emoji} ` : ''}{t.label}</option>
                ))}
              </select>
            </div>
          )}

          {type === 'automation' && (
            <>
              <div>
                <label className={labelClass}>Department</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                  {AUTOMATION_DEPARTMENTS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Apps Used</label>
                <input type="text" value={appsUsed} onChange={(e) => setAppsUsed(e.target.value)} className={inputClass} placeholder="e.g. JobTread, QuickBooks" />
              </div>
              <div>
                <label className={labelClass}>Workflow Steps</label>
                {workflowSteps.map((step, i) => (
                  <div key={i} className="mb-2 space-y-1">
                    <input
                      type="text"
                      value={step.title}
                      onChange={(e) => {
                        const next = [...workflowSteps];
                        next[i] = { ...next[i], title: e.target.value };
                        setWorkflowSteps(next);
                      }}
                      className={inputClass}
                      placeholder={`Step ${i + 1} title`}
                    />
                    <input
                      type="text"
                      value={step.description}
                      onChange={(e) => {
                        const next = [...workflowSteps];
                        next[i] = { ...next[i], description: e.target.value };
                        setWorkflowSteps(next);
                      }}
                      className={inputClass}
                      placeholder="Step description (optional)"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setWorkflowSteps([...workflowSteps, { title: '', description: '' }])}
                  className="text-sm text-red-600 hover:underline"
                >
                  + Add step
                </button>
              </div>
            </>
          )}

          {type === 'jobtread-import' && (
            <div>
              <label className={labelClass}>{fileKind === 'json' ? 'JSON File' : 'CSV File'}</label>
              <input type="file" accept={acceptFile} onChange={(e) => setFile(e.target.files?.[0] || null)} className={inputClass} required />
            </div>
          )}

          {type === 'claude-skill' && (
            <div>
              <label className={labelClass}>.skill File</label>
              <input type="file" accept={acceptFile} onChange={(e) => setFile(e.target.files?.[0] || null)} className={inputClass} required />
            </div>
          )}

          {type === 'app' && (
            <>
              <div>
                <label className={labelClass}>App Folder (.zip)</label>
                <input type="file" accept=".zip" onChange={(e) => setFolderZip(e.target.files?.[0] || null)} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Instructions (.pdf, .docx, .doc)</label>
                <input type="file" accept=".pdf,.docx,.doc" onChange={(e) => setInstructionsFile(e.target.files?.[0] || null)} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>
                  Demonstration video (.mp4){' '}
                  <span className={`font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    (optional)
                  </span>
                </label>
                <input
                  type="file"
                  accept=".mp4,video/mp4"
                  onChange={(e) => setDemoVideo(e.target.files?.[0] || null)}
                  className={inputClass}
                />
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Loops on the app&rsquo;s card so others can see it in action.
                </p>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className={`flex-1 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {submitting ? 'Uploading…' : 'Publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TechnologyUploadModal;
